import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppSelector } from '@/hooks/redux';
import { isBrandSelectableForFilters } from '@/utils/brandUtils';
import { sanitizeOptionalTaxonomyId, sanitizeTaxonomyIdList } from '@/utils/categoryFilterOptions';
import { resolveSelectedAudienceId } from '@/utils/audienceFilterParams';
import type { AudienceFilterItem } from '@/types/audienceFilters.types';

interface TabFiltersState {
  selectedBrandIds: string[];
  selectedNetwork: string;
  startDate?: string;
  endDate?: string;
  selectedCategoryIds?: string[];
  selectedSegmentIds?: string[];
  selectedAudienceId?: string;
}

export interface UseTabFiltersOptions {
  tabId: string;
  defaultBrandIds?: string[];
  defaultNetwork?: string;
  /**
   * Catálogo ya cargado (incl. vacío). `undefined` = aún no listo: no se resuelve la selección
   * para no borrar un id persistido antes de que llegue el catálogo.
   */
  audiences?: AudienceFilterItem[];
}

const EMPTY_IDS: string[] = [];

const INVALID_STRING_VALUES = new Set(['NaN', 'undefined', 'null']);

const sanitizeStringValue = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string' || INVALID_STRING_VALUES.has(value)) return fallback;
  return value;
};

export const clearAllTabFilters = () => {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('tab-filters-')) {
      localStorage.removeItem(key);
    }
  });
};

export const useTabFilters = ({
  tabId,
  defaultBrandIds = [],
  defaultNetwork = '',
  audiences,
}: UseTabFiltersOptions) => {
  const { brands: userBrands } = useAppSelector(state => state.auth);

  const [filters, setFilters] = useState<TabFiltersState>(() => {
    try {
      const saved = localStorage.getItem(`tab-filters-${tabId}`);
      if (saved) {
        const { selectedCategoryId, selectedSegmentId, ...parsed } = JSON.parse(saved);
        return {
          ...parsed,
          selectedNetwork: sanitizeStringValue(parsed.selectedNetwork, defaultNetwork),
          selectedCategoryIds: sanitizeTaxonomyIdList(
            parsed.selectedCategoryIds ?? selectedCategoryId
          ),
          selectedSegmentIds: sanitizeTaxonomyIdList(
            parsed.selectedSegmentIds ?? selectedSegmentId
          ),
          selectedAudienceId: sanitizeOptionalTaxonomyId(parsed.selectedAudienceId),
        };
      }
    } catch {
      localStorage.removeItem(`tab-filters-${tabId}`);
    }
    return {
      selectedBrandIds: defaultBrandIds,
      selectedNetwork: defaultNetwork,
      startDate: undefined,
      endDate: undefined,
    };
  });

  /** Catálogo: solo filas con código BQ no vacío (id o account_group_code). */
  const selectableBrands = useMemo(
    () => userBrands.filter(isBrandSelectableForFilters),
    [userBrands]
  );

  const validBrandIdSet = useMemo(
    () => new Set(selectableBrands.map(b => b.id)),
    [selectableBrands]
  );

  const allSelectableIds = useMemo(() => selectableBrands.map(b => b.id), [selectableBrands]);

  const effectiveBrandIds = useMemo(() => {
    if (userBrands.length === 0) return filters.selectedBrandIds;

    const valid = filters.selectedBrandIds.filter(id => validBrandIdSet.has(id));
    if (valid.length > 0) return valid;

    const fromDefaults = defaultBrandIds.filter(id => validBrandIdSet.has(id));
    if (fromDefaults.length > 0) return fromDefaults;

    if (allSelectableIds.length > 0) return allSelectableIds;

    return [];
  }, [
    filters.selectedBrandIds,
    validBrandIdSet,
    defaultBrandIds,
    allSelectableIds,
    userBrands.length,
  ]);

  useEffect(() => {
    const current = filters.selectedBrandIds;
    if (
      effectiveBrandIds.length !== current.length ||
      effectiveBrandIds.some((id, i) => id !== current[i])
    ) {
      setFilters(prev => ({ ...prev, selectedBrandIds: effectiveBrandIds }));
    }
  }, [effectiveBrandIds, filters.selectedBrandIds]);

  useEffect(() => {
    if (audiences === undefined) return;
    const resolved = resolveSelectedAudienceId(audiences, filters.selectedAudienceId);
    if (resolved !== filters.selectedAudienceId) {
      setFilters(prev => ({ ...prev, selectedAudienceId: resolved }));
    }
  }, [audiences, filters.selectedAudienceId]);

  useEffect(() => {
    localStorage.setItem(`tab-filters-${tabId}`, JSON.stringify(filters));
  }, [tabId, filters]);

  const brandOptions = useMemo(
    () => selectableBrands.map(brand => ({ value: brand.id, label: brand.name })),
    [selectableBrands]
  );

  const socialNetworks = useMemo(
    () => [
      { value: '', label: 'All Networks' },
      { value: 'tiktok', label: 'TikTok' },
      { value: 'instagram', label: 'Instagram' },
      { value: 'youtube', label: 'YouTube' },
      { value: 'facebook', label: 'Facebook' },
      { value: 'x', label: 'X' },
      { value: 'linkedin', label: 'LinkedIn' },
      { value: 'pinterest', label: 'Pinterest' },
    ],
    []
  );

  const handleBrandChange = useCallback((brandIds: string[]) => {
    setFilters(prev => ({ ...prev, selectedBrandIds: brandIds }));
  }, []);

  const handleNetworkChange = useCallback((network: string) => {
    setFilters(prev => ({ ...prev, selectedNetwork: sanitizeStringValue(network, '') }));
  }, []);

  const handleDateRangeChange = useCallback((startDate?: string, endDate?: string) => {
    setFilters(prev => ({ ...prev, startDate, endDate }));
  }, []);

  const handleCategoryChange = useCallback((categoryIds: string[]) => {
    setFilters(prev => ({ ...prev, selectedCategoryIds: categoryIds }));
  }, []);

  const handleSegmentChange = useCallback((segmentIds: string[]) => {
    setFilters(prev => ({ ...prev, selectedSegmentIds: segmentIds }));
  }, []);

  const handleAudienceChange = useCallback((audienceId: string) => {
    setFilters(prev => ({ ...prev, selectedAudienceId: audienceId }));
  }, []);

  const getFilterParams = useCallback(() => {
    const params: Record<string, unknown> = {
      brand_ids: effectiveBrandIds,
    };

    if (filters.startDate) {
      params.start_date = filters.startDate;
    }

    if (filters.endDate) {
      params.end_date = filters.endDate;
    }

    const network = sanitizeStringValue(filters.selectedNetwork, '');
    if (network) {
      params.platform_codes = [network.trim().toLowerCase()];
    }

    return params;
  }, [effectiveBrandIds, filters]);

  const clearFilters = useCallback(() => {
    const safeDefaults = defaultBrandIds.filter(id => validBrandIdSet.has(id));
    setFilters({
      selectedBrandIds: safeDefaults.length > 0 ? safeDefaults : allSelectableIds,
      selectedNetwork: defaultNetwork,
      startDate: undefined,
      endDate: undefined,
      selectedCategoryIds: [],
      selectedSegmentIds: [],
      selectedAudienceId: resolveSelectedAudienceId(audiences, undefined),
    });
  }, [defaultBrandIds, defaultNetwork, validBrandIdSet, allSelectableIds, audiences]);

  return {
    selectedBrandIds: effectiveBrandIds,
    selectedNetwork: filters.selectedNetwork,
    startDate: filters.startDate,
    endDate: filters.endDate,
    selectedCategoryIds: filters.selectedCategoryIds ?? EMPTY_IDS,
    selectedSegmentIds: filters.selectedSegmentIds ?? EMPTY_IDS,
    selectedAudienceId: filters.selectedAudienceId,
    brandOptions,
    socialNetworks,
    handleBrandChange,
    handleNetworkChange,
    handleCategoryChange,
    handleSegmentChange,
    handleAudienceChange,
    setDateRange: handleDateRangeChange,
    clearFilters,
    getFilterParams,
  };
};
